import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Modal } from 'antd';
import { setSubAccountSignId } from 'utils/storages';
import { Account } from 'interfaces/auth';
import { auth, setSessionExpiredHandler } from 'utils/auth';
import type { LoginResponse } from '@vendys/web-lib';
import errorHandler from 'apis/errorHandler';
import { useLogin, useLogout } from 'hooks/useLogin';

interface AuthContextInterface {
  authenticated: boolean;
  account: Account | undefined;
  setAccount: (v: Account | undefined) => void;
  signIn: (username: string, password: string, autoLogin: boolean, callback?: (response: LoginResponse) => void) => void;
  signOut: (callback?: VoidFunction) => void;
  isSignInLoading: boolean;
  isSignOutLoading: boolean;
}

export const AuthContext = React.createContext<AuthContextInterface>({
  isSignInLoading: false,
  isSignOutLoading: false,
  signIn: () => {},
  signOut: () => {},
  authenticated: false,
  account: undefined,
  setAccount: () => {}
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [authenticated, setAuthenticated] = useState(auth.isAuthenticated());
  const [account, setAccountState] = useState<Account | undefined>(auth.getAccount() ?? undefined);
  const { mutate: signInMutate, isLoading: isSignInLoading } = useLogin();
  const { mutateAsync: signOutMutate, isLoading: isSignOutLoading } = useLogout();

  // 세션 만료(인터셉터 refresh 갱신 실패) 처리
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setAuthenticated(false);
      setAccountState(undefined);
      queryClient.clear();
      navigate('/', { replace: true });
      Modal.warning({
        content: (
          <>
            세션이 만료되었습니다.
            <br />
            다시 로그인해 주세요.
          </>
        ),
        icon: null,
        okText: '확인',
        className: 'error-handler-modal',
        zIndex: 20000
      });
    });
    return () => setSessionExpiredHandler(null);
  }, [navigate, queryClient]);

  const setAccount = useCallback((newAccount: Account | undefined) => {
    if (newAccount) auth.setAccount(newAccount);
    setAccountState(newAccount);
  }, []);

  const signIn = useCallback(
    (username: string, password: string, autoLogin: boolean, callback?: (response: LoginResponse) => void) => {
      signInMutate(
        { username, password, autoLogin },
        {
          onSuccess: (response: LoginResponse) => {
            setAuthenticated(true);
            setAccountState(response.account);
            callback && callback(response);
          },
          onError: (error: any) => {
            const status = error?.cause?.response?.data?.status ?? error?.response?.data?.status;
            if (status === -511) {
              setSubAccountSignId(username);
              return;
            }
            // 로그인 실패 알림(antd Modal). 라이브러리 login은 authClient 인터셉터를 안 거치므로 직접 호출
            // AuthError의 원본 AxiosError는 cause에 담겨 있음
            errorHandler(error?.cause ?? error);
          }
        }
      );
    },
    [signInMutate]
  );

  const signOut = useCallback(
    async (callback?: VoidFunction) => {
      await signOutMutate(undefined, { onSuccess: callback });
      setAuthenticated(false);
      setAccountState(undefined);
      if (!callback) navigate('/');
    },
    [navigate, signOutMutate]
  );

  const value = useMemo(
    () => ({
      authenticated,
      account,
      setAccount,
      signIn,
      isSignInLoading,
      signOut,
      isSignOutLoading
    }),
    [account, authenticated, isSignInLoading, isSignOutLoading, setAccount, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
